import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiImage, FiPlus } from 'react-icons/fi';
import api, { getImageUrl } from '../services/api';
import { useContent } from '../context/ContentContext';
import './GalleryPage.css';

const GalleryPage = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const { content } = useContent();
  const ITEMS_PER_PAGE = 8;

  const categories = [
    { id: 'all', label: 'Tümü' },
    ...(content.gallery?.categories || [])
  ];

  useEffect(() => {
    // Filtre değiştiğinde state'leri sıfırla ve yeniden yükle
    setImages([]);
    setPage(1);
    setHasMore(true);
    
    const loadInitial = async () => {
      try {
        setLoading(true);
        const params = {
          page: 1,
          limit: ITEMS_PER_PAGE
        };

        if (filter && filter !== 'all') {
          params.category = filter;
        }

        const response = await api.get('/upload/gallery', { params });
        
        setImages(response.data.images);
        setHasMore(1 < response.data.totalPages);
      } catch (error) {
        console.error('Galeri yüklenemedi:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitial();
  }, [filter]); // Sadece filter değiştiğinde çalışır

  // Sayfa değiştirmek için ayrı fonksiyon (Load More butonu için)
  const loadMoreImages = async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      
      const params = {
        page: nextPage,
        limit: ITEMS_PER_PAGE
      };

      if (filter && filter !== 'all') {
        params.category = filter;
      }

      const response = await api.get('/upload/gallery', { params });
      const newImages = response.data.images;
      const totalPages = response.data.totalPages;

      setImages(prev => [...prev, ...newImages]);
      setPage(nextPage);
      setHasMore(nextPage < totalPages);

    } catch (error) {
      console.error('Galeri yüklenemedi:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadMoreImages();
    }
  };

  return (
    <div className="gallery-page">
      {/* Hero */}
      <section className="gallery-hero">
        <div className="gallery-hero-bg"></div>
        <div className="container">
          <motion.div 
            className="gallery-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1>Galeri</h1>
            <p>Kurumumuzdan kareler ve başarı hikayelerimiz</p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section section">
        <div className="gallery-content">
          
          {/* Filters */}
          <div className="gallery-filters">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`filter-btn ${filter === cat.id ? 'active' : ''}`}
                onClick={() => setFilter(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="gallery-loading">
              <div className="spinner"></div>
              <p>Yükleniyor...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="gallery-empty">
              <FiImage />
              <p>Bu kategoride henüz fotoğraf bulunmuyor.</p>
            </div>
          ) : (
            <>
              <motion.div 
                className="gallery-grid"
                layout
              >
                <AnimatePresence mode='popLayout'>
                  {images.map((image, index) => (
                    <motion.div
                      key={image._id}
                      className="gallery-item"
                      layout
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                      onClick={() => setSelectedImage(image)}
                    >
                      <div className="gallery-image-wrapper">
                        {image.imageUrl ? (
                          <img src={getImageUrl(image.imageUrl)} alt={image.title} loading="lazy" />
                        ) : (
                          <div className="image-placeholder">
                            <FiImage />
                            <span>{image.title}</span>
                          </div>
                        )}
                      </div>
                      <div className="gallery-info">
                        <h3>{image.title}</h3>
                        <span className="gallery-category">
                          {categories.find(c => c.id === image.category)?.label || image.category || 'Genel'}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>

              {/* Load More Button */}
              {hasMore && (
                <div className="gallery-load-more">
                  <button 
                    className="btn btn-outline" 
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <span className="spinner-small"></span> Yükleniyor...
                      </>
                    ) : (
                      <>
                        <FiPlus /> Daha Fazla Göster
                      </>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div 
            className="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <button className="lightbox-close" onClick={() => setSelectedImage(null)}>
              <FiX />
            </button>
            <motion.div 
              className="lightbox-content"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={e => e.stopPropagation()}
            >
              {selectedImage.imageUrl ? (
                <img src={getImageUrl(selectedImage.imageUrl)} alt={selectedImage.title} />
              ) : (
                <div className="lightbox-placeholder">
                  <FiImage />
                  <span>{selectedImage.title}</span>
                </div>
              )}
              <div className="lightbox-info">
                <h3>{selectedImage.title}</h3>
                {selectedImage.description && <p>{selectedImage.description}</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GalleryPage;